
import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { initializeAudioContext, playSoftSuccessSound, closeAudioContext } from "./useScanditScanner/audioUtils";
import { cleanupScanditSession, ScanditRefs } from "./useScanditScanner/cleanupUtils";
import { createBarcodeDetectionHandler } from "./useScanditScanner/barcodeHandler";
import { initializeScanditSDK } from "./useScanditScanner/scannerInit";
import { startScanditScanning, stopScanditScanning } from "./useScanditScanner/scannerOperations";

interface UseScanditScannerProps {
  onScanResult: (barcode: string) => void;
  onClose: () => void;
}

export const useScanditScanner = ({ onScanResult, onClose }: UseScanditScannerProps) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [detectionStrength, setDetectionStrength] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [initializationStep, setInitializationStep] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  
  const videoRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  
  // Core SDK references
  const contextRef = useRef<any>(null);
  const cameraRef = useRef<any>(null);
  const barcodeCaptureRef = useRef<any>(null);
  const viewRef = useRef<any>(null);
  const listenerRef = useRef<any>(null);
  const ScanditCoreRef = useRef<any>(null);
  const ScanditBarcodeRef = useRef<any>(null);
  const isCleaningUpRef = useRef(false);
  const hasDetectedBarcodeRef = useRef(false);
  
  const { toast } = useToast();

  const scanditRefs: ScanditRefs = {
    contextRef,
    cameraRef,
    barcodeCaptureRef,
    viewRef,
    listenerRef,
    ScanditCoreRef,
    ScanditBarcodeRef,
    isCleaningUpRef
  };

  useEffect(() => {
    const initialize = async () => {
      await initializeScanditSDK(scanditRefs, setInitializationStep, setError, setIsInitialized);
      audioContextRef.current = await initializeAudioContext();
    };
    
    initialize();
    
    return () => {
      const cleanup = async () => {
        await cleanupScanditSession(scanditRefs, videoRef);
        await closeAudioContext(audioContextRef);
      };
      cleanup();
    };
  }, []);

  const stopScanning = async () => {
    await stopScanditScanning(scanditRefs, videoRef, setIsScanning, setDetectionStrength);
  };

  const handleBarcodeDetected = createBarcodeDetectionHandler(
    onScanResult,
    onClose,
    audioContextRef,
    soundEnabled,
    hasDetectedBarcodeRef,
    stopScanning,
    toast
  );

  const startScanning = async () => {
    await startScanditScanning(
      scanditRefs,
      videoRef,
      isInitialized,
      handleBarcodeDetected,
      setDetectionStrength,
      setError,
      setIsScanning,
      hasDetectedBarcodeRef,
      toast
    );
  };

  const toggleTorch = async () => {
    if (!cameraRef.current || !isScanning) return;
    
    try {
      // Note: Torch functionality depends on device support
      toast({
        title: "Torch Feature",
        description: "Torch control not implemented in this version",
      });
    } catch (error) {
      console.error('Torch error:', error);
    }
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
    toast({
      title: soundEnabled ? "🔇 Sound Disabled" : "🔊 Sound Enabled",
      description: soundEnabled ? "Success sounds turned off" : "Success sounds turned on",
      duration: 2000,
    });
    
    if (!soundEnabled) {
      setTimeout(() => playSoftSuccessSound(audioContextRef, true), 100);
    }
  };

  const simulateScan = () => {
    console.log('Simulating Scandit barcode scan...');
    const mockBarcodes = [
      "1234567890123",
      "SCANDIT123456",
      "9780123456789"
    ];
    const randomBarcode = mockBarcodes[Math.floor(Math.random() * mockBarcodes.length)];
    handleBarcodeDetected(randomBarcode, 95);
  };

  const retryInitialization = () => {
    setError(null);
    setRetryCount(prev => prev + 1);
    setInitializationStep('');
    contextRef.current = null;
    cameraRef.current = null;
    ScanditCoreRef.current = null;
    ScanditBarcodeRef.current = null;
    initializeScanditSDK(scanditRefs, setInitializationStep, setError, setIsInitialized);
  };

  return {
    isScanning,
    error,
    isInitialized,
    torchEnabled: false,
    detectionStrength,
    soundEnabled,
    initializationStep,
    retryCount,
    videoRef,
    startScanning,
    stopScanning,
    toggleTorch,
    toggleSound,
    simulateScan,
    retryInitialization
  };
};
